import { CopyButton } from "@expert/common-ui";
import { useCustomerAvatar } from "@expert/contentful";
import { AllowedPartners, useExpertUniversityConfiguration, useExpertUniversityStore } from "@expert/expert-university";
import { EligibilityStatus, SaleView } from "@expert/sales-tools";
import { Flex, Group, Image, Stack, Tabs, Text } from "@mantine/core";

import { PhoneNumber, ProperName } from "@/common-ui";
import { Sidebar as StaticSalesChecklist } from "@/expert-university/components/Sidebar";
import { useSessionId, useTypedMostRecentTask } from "@/sdk";
import { removeCountryCode } from "@/shared-utils";

import classes from "./MockCustomerPanel.module.css";

function ShieldIcon() {
    return (
        <svg width="12" height="13.5" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.50002 3.61522V8.69122C1.50005 11.9756 3.52268 14.9207 6.58802 16.0999L7.28202 16.3666C7.74418 16.5443 8.25586 16.5443 8.71802 16.3666L9.41202 16.0999C12.4774 14.9207 14.5 11.9756 14.5 8.69122V3.61522C14.5026 3.22505 14.2761 2.86964 13.9214 2.70722C12.0546 1.89242 10.0368 1.481 8.00002 1.49989C5.96326 1.481 3.94546 1.89242 2.07869 2.70722C1.72392 2.86964 1.49741 3.22505 1.50002 3.61522Z"
                stroke="#A5AAAF"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M5.3335 9.33325L7.3335 10.9999L10.6668 6.33325"
                stroke="#A5AAAF"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function MockCustomerProfile() {
    const { voiceTask } = useTypedMostRecentTask();

    const profileId = useExpertUniversityStore.getState().profileId;

    const sessionId = useSessionId();

    const customerAvatarUrl = useCustomerAvatar(sessionId);
    if (!voiceTask) {
        return null;
    }
    return (
        <Flex className={classes.topSection}>
            <Group justify="space-between" gap="0" align="top" wrap="nowrap">
                <Group gap="0" wrap="nowrap">
                    <Image className={classes.profilePicture} data-testid="customer-avatar" src={customerAvatarUrl} />

                    <Stack gap="0">
                        <ProperName
                            data-testid="customer-name"
                            size="sm"
                            truncate
                            name={profileId?.replaceAll("_", " ")}
                            className={`${classes.name} fs-mask`}
                        />

                        <Group gap="0">
                            <Text
                                w="max-content"
                                className={`${classes.customerIdentifier} fs-mask data-testid="customer-mdn"`}
                                size="sm"
                            >
                                <PhoneNumber mdn={voiceTask.mdn} />
                            </Text>
                            {/* TODO: migrate to getMdnClipboardValue — needs regression testing from codeowners */}
                            <CopyButton value={removeCountryCode(voiceTask.mdn)} />
                        </Group>
                        <Flex gap="4px" w="max-content">
                            <Text c="dark.2" size="xxs" pr="4px">
                                {`Protected for 1+ years`}
                            </Text>
                            <ShieldIcon />
                        </Flex>
                    </Stack>
                </Group>
            </Group>
        </Flex>
    );
}

function MockSalesFlow() {
    const { trainingPartner: partner } = useExpertUniversityConfiguration();

    return (
        <Tabs
            classNames={{ root: classes.root }}
            radius={0}
            data-testid="multi-product-tab-container"
            defaultValue="homePlan"
        >
            <Tabs.List grow>
                <Tabs.Tab className={classes.tab} value="homePlan">
                    <Flex className={classes.tabHeader} justify="center">
                        <Text size="xs">Primary</Text>
                    </Flex>
                </Tabs.Tab>

                <Tabs.Tab className={classes.tab} value="resources">
                    <Text size="xs">Resources</Text>
                </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel className={classes.tabPanel} value="homePlan">
                <SaleView planType="Other" status={EligibilityStatus.Unknown} />
            </Tabs.Panel>

            <Tabs.Panel className={classes.tabPanel} value="resources">
                <StaticSalesChecklist partner={partner as AllowedPartners} />
            </Tabs.Panel>
        </Tabs>
    );
}

export function MockCustomerPanel() {
    return (
        <Flex className={classes.customerPanel} data-testid="customer-panel">
            <Flex className={classes.responsivePanel}>
                <MockCustomerProfile />
                <MockSalesFlow />
            </Flex>
        </Flex>
    );
}
